import React, {Suspense, useCallback, useEffect, useRef, useState} from 'react';
import {
  ActivityIndicator,
  PermissionsAndroid,
  Platform,
  StyleSheet,
  View,
} from 'react-native';
import {Camera} from 'react-native-camera-kit';
import type {NativeStackScreenProps} from '@react-navigation/native-stack';
import {useTranslation} from 'react-i18next';

import type {DevicesStackParamList} from '@/app/routes';
import {parseScannedDevice} from '@/entities/machine/lib';
import {AppButton} from '@/shared/ui/button';
import {Card, IconBubble} from '@/shared/ui/card';
import {Icon} from '@/shared/ui/icons';
import {AppInput} from '@/shared/ui/input';
import {Page} from '@/shared/ui/page';
import {AppText} from '@/shared/ui/text';
import {radii, spacing, useTheme} from '@/shared/ui/theme';

/**
 * Step one of adopting a terminal: read the pairing code the device shows as a QR.
 *
 * The camera is the fast path, but never the only one — a phone can be denied the
 * camera, and the Android emulator's virtual camera cannot render a QR at all. So a
 * manual code field is always present, and the whole flow is reachable without ever
 * granting the permission. Reading a code and typing one both land on the same
 * ConfirmAdopt screen; the code is not validated here, that is the confirm step's job.
 */
type Props = NativeStackScreenProps<DevicesStackParamList, 'ScanDevice'>;

type PermissionState = 'checking' | 'granted' | 'denied';

const styles = StyleSheet.create({
  viewport: {
    height: 320,
    borderRadius: radii.xl,
    overflow: 'hidden',
    alignItems: 'center',
    justifyContent: 'center',
    gap: spacing(3),
  },
  camera: {position: 'absolute', top: 0, left: 0, right: 0, bottom: 0},
  denied: {textAlign: 'center', paddingHorizontal: spacing(6)},
  manual: {gap: spacing(3)},
});

export function ScanDeviceScreen({navigation}: Props) {
  const {t} = useTranslation();
  const p = useTheme();
  const [permission, setPermission] = useState<PermissionState>('checking');
  const [code, setCode] = useState('');
  // Guards against onReadCode firing several times for the same code before the
  // navigation transition unmounts the camera.
  const handledRef = useRef(false);

  useEffect(() => {
    let active = true;
    void (async () => {
      if (Platform.OS !== 'android') {
        // iOS asks for the permission itself when the camera view mounts.
        if (active) {
          setPermission('granted');
        }
        return;
      }
      try {
        const result = await PermissionsAndroid.request(
          PermissionsAndroid.PERMISSIONS.CAMERA,
          {
            title: t('home.adopt.permission.title'),
            message: t('home.adopt.permission.message'),
            buttonPositive: t('home.adopt.permission.grant'),
            buttonNegative: t('actions.cancel'),
          },
        );
        if (active) {
          setPermission(
            result === PermissionsAndroid.RESULTS.GRANTED ? 'granted' : 'denied',
          );
        }
      } catch {
        if (active) {
          setPermission('denied');
        }
      }
    })();
    return () => {
      active = false;
    };
  }, [t]);

  // Coming back from ConfirmAdopt re-arms the scanner so the next code is read.
  useEffect(
    () => navigation.addListener('focus', () => {
      handledRef.current = false;
    }),
    [navigation],
  );

  const openConfirm = useCallback(
    (scanned: string) => {
      // A current terminal encodes `{panel}/machines/SVFID-…`; one on the
      // previous firmware encodes `{panel}/enroll/{code}`. Either URL, either
      // bare path, or either identifier typed by hand all work.
      const target = parseScannedDevice(scanned);
      if (!target || handledRef.current) {
        return;
      }
      handledRef.current = true;
      navigation.navigate('ConfirmAdopt', target);
    },
    [navigation],
  );

  return (
    <Page
      scroll
      width="form"
      title={t('home.adopt.scan.title')}
      subtitle={t('home.adopt.scan.hint')}
      testID="adopt-scan">

      {/* The viewport is a solid dark well rather than an outlined box: a camera
          preview reads as a lens, and a lens is not a form field. */}
      <View style={[styles.viewport, {backgroundColor: p.muted}]}>
        {permission === 'checking' ? (
          <ActivityIndicator size="large" color={p.primary} />
        ) : permission === 'granted' ? (
          <Suspense
            fallback={<ActivityIndicator size="large" color={p.primary} />}>
            <Camera
              style={styles.camera}
              scanBarcode
              showFrame
              laserColor={p.primary}
              frameColor={p.primary}
              onReadCode={event =>
                openConfirm(event.nativeEvent.codeStringValue)
              }
            />
          </Suspense>
        ) : (
          <>
            <IconBubble tone={p.card} size={64} square>
              <Icon name="qr" size={30} color={p.mutedForeground} />
            </IconBubble>
            <AppText variant="body" color={p.mutedForeground} style={styles.denied}>
              {t('home.adopt.scan.denied')}
            </AppText>
          </>
        )}
      </View>

      <Card padding={4} style={styles.manual}>
        <AppText variant="label">{t('home.adopt.scan.manualLabel')}</AppText>
        <AppInput
          value={code}
          onChangeText={setCode}
          placeholder={t('home.adopt.scan.manualPlaceholder')}
          autoCapitalize="characters"
          autoCorrect={false}
          onSubmitEditing={() => openConfirm(code)}
          returnKeyType="go"
        />
        {/* Поле принимало обе формы и раньше — врала только подпись. Здесь
            сказано, чем они отличаются: номер годится, когда у устройства
            никого нет, код доказывает, что кто-то стоит рядом. */}
        <AppText variant="caption" color={p.mutedForeground}>
          {t('home.adopt.scan.manualHint')}
        </AppText>
        <AppButton
          title={t('home.adopt.scan.manualSubmit')}
          tone="soft"
          disabled={code.trim().length === 0}
          onPress={() => openConfirm(code)}
        />
      </Card>
    </Page>
  );
}
